import './downloads.css';
import { searchBox } from 'blocks/shared/search-box/search-box.ts';
import type { FilterValues } from 'blocks/shared/search-filters/search-filters.ts';
import { searchTitle } from 'blocks/shared/search-title/search-title.ts';
import { fetchPlaceholders, getMetadata } from 'scripts/aem.js';
import { div, search } from 'scripts/dom-helpers.js';
import type { DownloadSortOptions } from './download-search-result.ts';
import { DownloadsSearchResults } from './download-search-result.ts';
import type {
	Download,
	DownloadLanguage,
	DownloadProduct,
	DownloadType,
} from './downloads.types.ts';
import {
	groupDownloadsByFields,
	fetchData,
	filterAndSortDownloads,
	createSearchFilters,
} from './downloads.utils.ts';

const PAGE_SIZE = 20;

export default async function decorate(block: Element) {
	const disableSoftwareLicense = block.classList.contains(
		'disable-software-license',
	);
	const disableTermsPrivacy = block.classList.contains('disable-terms-privacy');

	let downloadsLink: string | undefined;
	let downloadProductsLink: string | undefined;
	let downloadTypesLink: string | undefined;
	let downloadLanguagesLink: string | undefined;

	const pageTitle = block.firstElementChild?.firstElementChild;
	const searchTitleElement = pageTitle?.children[0];

	[...block.querySelectorAll<HTMLAnchorElement>('a')].forEach((link, index) => {
		switch (index) {
			case 0:
				downloadsLink = link.href;
				return;
			case 1:
				downloadProductsLink = link.href;
				return;
			case 2:
				downloadTypesLink = link.href;
				return;
			case 3:
				downloadLanguagesLink = link.href;
				return;
		}
	});

	block.innerHTML = '';

	if (
		!downloadsLink ||
		!downloadProductsLink ||
		!downloadTypesLink ||
		!downloadLanguagesLink
	)
		throw new Error('Missing required links');

	const [rawDownloads, types, languages, products, placeholders] =
		await Promise.all([
			fetchData<Download>(downloadsLink),
			fetchData<DownloadType>(downloadTypesLink),
			fetchData<DownloadLanguage>(downloadLanguagesLink),
			fetchData<DownloadProduct>(downloadProductsLink),
			fetchPlaceholders(),
		]);

	const downloads = groupDownloadsByFields(rawDownloads, placeholders);

	let sortOptions: DownloadSortOptions = {};
	let currentFilters: FilterValues[] = [];
	let currentQuery = '';

	const pageSizeMetadata = Number(getMetadata('page-size'));

	const performSearch = () => {
		renderResults(
			filterAndSortDownloads(
				currentQuery,
				downloads,
				currentFilters,
				sortOptions,
			),
		);
	};

	const {
		element: searchResultsElement,
		renderResults,
		sortAndRender,
	} = new DownloadsSearchResults({
		placeholders: {
			...placeholders,
			searchLoadMore: placeholders.downloadsLoadMore,
			searchNoResults: placeholders.downloadsNoResults,
			searchTotalLabel: placeholders.downloadsProducts || 'Products',
			searchTotalOf: placeholders.downloadsOf,
		},
		searchResultPageLength:
			isNaN(pageSizeMetadata) || pageSizeMetadata <= 0
				? PAGE_SIZE
				: pageSizeMetadata,
		showResultTotal: true,
		linkPrefix: getMetadata('download-prefix'),
		onSort: (updatedSortOptions: DownloadSortOptions) => {
			sortOptions = updatedSortOptions;
			performSearch();
		},
		disableSoftwareLicense,
		disableTermsPrivacy,
	});

	const filters = createSearchFilters(
		products,
		types,
		languages,
		placeholders,
		(filters) => {
			currentFilters = filters;
			performSearch();
		},
	);
	currentFilters = filters.getFiltersFromSearch();

	const onSearch = (searchValue: string) => {
		currentQuery = searchValue;
		performSearch();
	};
	const onClearSearch = () => {
		currentQuery = '';
		performSearch();
	};

	const searchTitleBlock = searchTitle({
		titleElement: searchTitleElement,
		searchBox: searchBox({ placeholders, onSearch, onClear: onClearSearch }),
	});

	block.append(
		search(
			{ class: 'search' },
			searchTitleBlock,
			document.createElement('hr'),
			div(
				{ class: 'downloads__filters-results' },
				filters.element,
				searchResultsElement,
			),
		),
	);
	sortAndRender('version');
}
